import React, { useState, useEffect, useRef, useCallback, ReactNode, useMemo } from "react";
import classNames from "classnames";

import useValidate from "../../validations/useValidate";
import { Icon, Spinner } from "../";

import Styles from "./Dropdown.module.scss";
import { useTheme } from "../../styles/useTheme";
import validations from "../../validations/validations";

type Option = {
  text: string;
  value: string;
};

type DropdownProps = {
  id?: string;
  // Make sure to use unique names for each dropdown on the page because it can cause issues with the auto scroll when using the same name
  name?: string;
  value: unknown;
  options: Option[];
  placeholder?: string;
  onChange: (value: string) => void;
  onValidate?: (error: string | undefined) => void;
  onClick?: () => void;
  onBlur?: () => void;
  onFocus?: () => void;
  fullWidth?: boolean;
  validate?: ((value: unknown) => string | undefined) | Array<(value: unknown) => string | undefined>;
  showValidation?: boolean;
  error?: boolean;
  errorMessage?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  title?: string | ReactNode;
  listOnTop?: boolean;
  required?: boolean;
};

const Dropdown = ({
  id,
  name,
  placeholder,
  value,
  options,
  onChange,
  onValidate,
  onClick,
  onBlur,
  onFocus,
  fullWidth,
  validate,
  showValidation,
  error,
  errorMessage,
  emptyMessage,
  className,
  disabled,
  loading,
  title,
  listOnTop,
  required,
}: DropdownProps) => {
  const { primaryColor } = useTheme();
  const [selectedItem, setSelectedItem] = useState<Option | undefined>(undefined);
  const input = useRef<HTMLInputElement>(null);
  const optionsBox = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [showValidationError, setShowValidationError] = useState(false);
  const refError = useRef<string | undefined>();

  const _validations = useMemo(() => {
    const arr = validate ? (Array.isArray(validate) ? validate : [validate]) : [];
    if (required) {
      arr.push(validations.required);
    }
    return arr?.length > 0 ? arr : undefined;
  }, [validate, required]);

  const currentError = useValidate(value, _validations);

  useEffect(() => {
    if (onValidate && refError.current !== currentError) {
      refError.current = currentError;
      onValidate(currentError);
    }
  }, [currentError, onValidate]);

  useEffect(() => {
    if (showValidation && !showValidationError) {
      setShowValidationError(true);
    }
  }, [showValidation, showValidationError]);

  const selectedOption = useCallback(
    (options: Option[]): Option | undefined =>
      options?.length ? options.find((option) => option.value === value) : undefined,
    [value]
  );

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [optionsVisible, setOptionsVisible] = useState<boolean>(false);
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options || []);
  const [searchValue, setSearchValue] = useState<string>(selectedOption(options)?.text || "");
  const [lastValue, setLastValue] = useState<string>();

  useEffect(() => {
    if (isFocused) {
      const preselected = options.find((d) => d.text === lastValue);
      if (preselected) {
        setSelectedItem(preselected);
      }
    }
    // eslint-disable-next-line
  }, [isFocused, lastValue, JSON.stringify(options)]);

  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (optionsVisible) {
          setOptionsVisible(false);
        }
      }
    },
    [optionsVisible]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);
    return () => document.removeEventListener("keydown", handleKeydown);
  }, [handleKeydown]);

  const clickOption = (option: Option) => {
    onChange(option.value);
    setSearchValue(option.text);
    setOptionsVisible(false);
  };

  const formatOption = (option: string) => {
    try {
      return option?.toUpperCase().replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "");
    } catch {
      return option;
    }
  };

  const filterOptions = (options: Option[], upperCasedQuery: string, regex: RegExp) => {
    const filteredOptions = options
      ?.filter((option) => {
        const formattedOption = formatOption(option.text);
        return formattedOption ? RegExp(regex).exec(formattedOption) !== null : false;
      })
      .sort((a, b) => {
        const optionA = formatOption(a.text);
        const optionB = formatOption(b.text);
        if (
          optionA.substring(0, upperCasedQuery.length).indexOf(upperCasedQuery) <
          optionB.substring(0, upperCasedQuery.length).indexOf(upperCasedQuery)
        )
          return 1;
        if (
          optionA.substring(0, upperCasedQuery.length).indexOf(upperCasedQuery) >
          optionB.substring(0, upperCasedQuery.length).indexOf(upperCasedQuery)
        )
          return -1;
        return 0;
      });

    return filteredOptions?.length > 0 ? filteredOptions : options;
  };

  const filter = (query: string) => {
    setSearchValue(query);
    const upperCasedQuery = formatOption(query?.toUpperCase());
    const regex = new RegExp(upperCasedQuery, "gi");
    filterOptions(options, upperCasedQuery, regex);
    setFilteredOptions(filterOptions(options, upperCasedQuery, regex));
  };

  useEffect(() => {
    if (options?.length === 1 && selectedOption(options) === undefined) {
      onChange(options[0].value);
    } else {
      setSearchValue(selectedOption(options)?.text || "");
    }
  }, [options, selectedOption, onChange]);

  const handleInputKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (optionsVisible) {
      const index = filteredOptions.findIndex((option) => option.value === selectedItem?.value);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (filteredOptions.length > 0) {
          const newIndex = index < filteredOptions.length - 1 ? index + 1 : 0;
          const newOption = filteredOptions[newIndex];
          setSelectedItem(newOption);
          document.querySelector(`#option_${name}_${newIndex}`)?.scrollIntoView({ block: "nearest" });
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (filteredOptions.length > 0) {
          const newIndex = index > 0 ? index - 1 : filteredOptions.length - 1;
          const newOption = filteredOptions[newIndex];
          setSelectedItem(newOption);
          document.querySelector(`#option_${name}_${newIndex}`)?.scrollIntoView({ block: "nearest" });
        }
      } else if (e.key === "Enter") {
        selectedItem && clickOption(selectedItem);
        setSelectedItem(undefined);
        setLastValue(selectedItem?.text);
      }
    } else if (!optionsVisible && isFocused) {
      setOptionsVisible(true);
    }
  };

  const handleOnBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (wrapperRef.current) {
      // If the focus is not on the dropdown wrapper (for example clicking outside)
      if (!wrapperRef.current?.contains(e.relatedTarget as HTMLInputElement)) {
        setOptionsVisible(false);
      }
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={classNames(Styles.formField, className, { [Styles.fullWidth]: fullWidth })}
      onBlur={handleOnBlur}
    >
      {title &&
        (React.isValidElement(title) ? (
          title
        ) : (
          <span className={classNames(Styles.title, { ["floatAbove"]: isFocused || optionsVisible || searchValue })}>
            {title}
          </span>
        ))}
      <div
        className={classNames(Styles.inputWrapper, Styles.withLabel, Styles.right, {
          [Styles.focus]: (isFocused || optionsVisible) && !disabled,
          [Styles.removeBorder]: optionsVisible,
          [Styles.disabled]: disabled,
          [Styles.error]: (error || currentError) && showValidationError,
          [Styles.listOnTop]: listOnTop,
        })}
      >
        {loading && <Spinner className={Styles.spinner} />}
        <input
          id={id}
          role="searchbox"
          ref={input}
          type="search"
          name={name}
          placeholder={placeholder}
          value={searchValue}
          autoComplete="chrome-off"
          onFocus={() => {
            onFocus && onFocus();
            setIsFocused(true);
            setOptionsVisible(true);
            setLastValue(searchValue);
            filter("");
          }}
          onBlur={() => {
            onBlur && onBlur();
            setIsFocused(false);
            setSearchValue(lastValue || "");
            if ((currentError || errorMessage) && !showValidationError) {
              setShowValidationError(true);
            }
          }}
          onChange={(e) => filter(e.currentTarget.value)}
          disabled={disabled}
          onKeyDown={handleInputKeydown}
        />
        <div role="button" className={classNames(Styles.label, primaryColor)} onClick={() => input.current?.focus()}>
          <Icon name="angleDown" />
        </div>
        <div
          id={`${name}_item_container`}
          role="listbox"
          aria-label={placeholder}
          aria-hidden={!optionsVisible || disabled}
          ref={optionsBox}
          className={classNames(Styles.optionsBox, {
            [Styles.visible]: optionsVisible && !disabled,
            [Styles.listOnTop]: listOnTop,
          })}
          tabIndex={-1}
        >
          {filteredOptions?.map((option, index) => (
            <div
              role="option"
              key={`option_${index}`}
              id={`option_${name}_${index}`}
              className={classNames(Styles.option, {
                [Styles.selected]: option.value === value,
                [Styles.hover]: selectedItem?.value === option.value,
              })}
              onClick={() => {
                onClick && onClick();
                clickOption(option);
              }}
            >
              {option.text}
            </div>
          ))}
          {!filteredOptions.length && emptyMessage && <div className={Styles.noOptions}>{emptyMessage}</div>}
        </div>
      </div>
      {(errorMessage || currentError) && showValidationError && (
        <div className={Styles.errorMessage} role={"alert"}>
          {errorMessage || currentError}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
